//@ts-nocheck
import React, { useCallback } from 'react';
import { Fund } from 'src/components/Exchange/types';
import config from 'src/config/config';
import {
  ADD_FUND_MODAL,
  MIN_FUND_ALERT_MODAL,
} from 'src/redux/modules/modals/modals.module';
import {
  COLLEGE_FUND_NOTE_PHONE_NUMBERS,
  FundNoteExchange,
  SystemCode,
  TOLL_FREE_NUMBER,
} from 'src/settings/settings';
import {
  getFundLimit,
  isCollegeExchange,
  isExchange,
} from 'src/utils/transactions-util';
import { isAPP } from 'src/utils/advice/advice-utility';
import { isRetirementFund } from 'src/utils/funds/funds-utility';
import { coalesce } from 'src/utils/utils';
import './FundListActionBar.scss';
import BuyFundListActionBarLink from './FundListActionBarLink';
import { getAvailableFundsWithSameRegistrationGroup } from '../../../redux/modules/exchangeTransaction/exchangeTransaction.selectors';
import { useSelector } from 'react-redux';

export interface FundListActionBarProps {
  isSalesforceRep: boolean;
  customerWebViewEnabled: boolean;
  actions;
  canAddNewFund: boolean;
  fundListActionBarLink;
  modalProps;
  userRegistrationTypeInfo;
  exchangeFromFunds: Fund[];
  selectedFund: Fund;
  eligibleFromFundForExchangeToNew: boolean;
}

const FundListActionBar: React.FC<FundListActionBarProps> = ({
  isSalesforceRep,
  customerWebViewEnabled,
  actions,
  canAddNewFund,
  exchangeFromFunds,
  fundListActionBarLink: FundListActionBarLink = BuyFundListActionBarLink,
  modalProps,
  selectedFund,
  userRegistrationTypeInfo,
  eligibleFromFundForExchangeToNew,
}) => {
  const clickHandler = useCallback(() => {
    const grossAmountDollars =
      exchangeFromFunds?.reduce((a, b) => a + (b.grossAmountDollars || 0), 0) ??
      0;

    let fundLimit = 0;
    if (
      isExchange() &&
      exchangeFromFunds.some((fund) => isAPP(fund.productType))
    ) {
      fundLimit = isRetirementFund(exchangeFromFunds[0]) ? 1000 : 2500;
    } else {
      fundLimit =
        (isExchange() || isCollegeExchange()) &&
        getFundLimit({ selectedFund, userRegistrationTypeInfo });
    }

    if (
      (isExchange() || isCollegeExchange()) &&
      grossAmountDollars < fundLimit
    ) {
      actions.openModal(MIN_FUND_ALERT_MODAL, { fundLimit });
    } else if (canAddNewFund) {
      actions.openModal(ADD_FUND_MODAL, modalProps);
    }
  }, [
    actions,
    canAddNewFund,
    exchangeFromFunds,
    modalProps,
    selectedFund,
    userRegistrationTypeInfo,
  ]);
  let checkEmptyUniqueId = true;
  let serviceFeature = false;
  let everyFundHasAServiceFeature = false;
  if (userRegistrationTypeInfo?.selectedRegistrationType && isExchange()) {
    checkEmptyUniqueId =
      userRegistrationTypeInfo?.selectedRegistrationType
        ?.modelAgreementUniqueIDs?.length > 0 ||
      isAPP(selectedFund.productType);
  }
  const availableToFundsWithSameRegistrationGroup = useSelector(
    getAvailableFundsWithSameRegistrationGroup
  );
  if (isExchange() || isCollegeExchange()) {
    serviceFeature = exchangeFromFunds?.some(
      (selectedFund) =>
        selectedFund.notes[FundNoteExchange.ACH_DIVIDEND] ||
        selectedFund.notes[FundNoteExchange.WITHIN_CURRENT_INVESTMENTS]
    );
  }
  everyFundHasAServiceFeature = getEveryFundHasAServiceFeature(
    availableToFundsWithSameRegistrationGroup
  );

  const phoneNumber = selectedFund
    ? coalesce(
        COLLEGE_FUND_NOTE_PHONE_NUMBERS[selectedFund.productType],
        TOLL_FREE_NUMBER
      )
    : TOLL_FREE_NUMBER;
  const exchangeEligibleForNew =
    (isExchange() && eligibleFromFundForExchangeToNew) || !isExchange();
  return (
    <div>
      {checkEmptyUniqueId &&
      !serviceFeature &&
      exchangeEligibleForNew &&
      !everyFundHasAServiceFeature ? (
        <a
          className="fund-list-action-bar-step2-action-bar e2e-nasu-link college-branded-fund-list-action-bar"
          href={
            canAddNewFund
              ? undefined
              : `${config.rmlUrl}/usis/nasu/account-finder`
          }
          id="e2e-step2-action-bar"
          rel="noopener noreferrer"
          target="_blank"
          onClick={clickHandler}
        >
          <FundListActionBarLink
            customerWebViewEnabled={customerWebViewEnabled}
            isSalesforceRep={isSalesforceRep}
          />
        </a>
      ) : (
        <span>
          {!customerWebViewEnabled && isSalesforceRep ? (
            <span></span>
          ) : (
            <p className="fund-list-action-bar-info-text">
              Looking to expand your portfolio? Call {phoneNumber} to add an
              investment to this account.
            </p>
          )}
        </span>
      )}
    </div>
  );
};

const getEveryFundHasAServiceFeature = (
  availableToFundsWithSameRegistrationGroup
) => {
  if (!isExchange()) {
    return false;
  }
  return availableToFundsWithSameRegistrationGroup?.every((selectedFund) => {
    return selectedFund?.fundLock?.foundFeatures?.some((feature) => {
      return (
        feature?.serviceID === SystemCode.STOP_PURCHASE ||
        feature?.serviceID === SystemCode.RETURN_MAIL_VERIFY_ADDRESS ||
        feature?.serviceID === SystemCode.ACH_DIVIDEND ||
        feature?.serviceID === SystemCode.PENALTY_WITHOLDING ||
        feature?.serviceID === SystemCode.AUTOMATED_SYSTEMS_STOP_ALL ||
        feature?.serviceID === SystemCode.DIVIDEND_MOVE ||
        feature?.serviceID === SystemCode.DIVIDEND_CHECK_TO_ALTERNATIVE_ADDRESS
      );
    });
  });
};

export default FundListActionBar;
